import { ShimmerText } from '@/components/reasoning/ShimmerText';
import { cx } from '@/utils/cx';

import type { ConversationRunState } from '../conversationRunState';
import {
  deriveConversationRunState,
  RUN_STATE_FAILED_LABEL,
  RUN_STATE_WORKING_LABEL,
} from '../conversationRunState';

interface ConversationRunStateLabelProps {
  lastChainFailed?: boolean | null;
  hasActiveChain?: boolean | null;
  state?: ConversationRunState;
  className?: string;
}

/**
 * Shared running/failed label for conversation list surfaces. Renders nothing
 * for idle conversations. Callers that have already derived the run state
 * (e.g. to also branch on it) can pass `state` directly to avoid a second
 * internal derivation; otherwise it's derived from `lastChainFailed`/`hasActiveChain`.
 */
export function ConversationRunStateLabel({
  lastChainFailed,
  hasActiveChain,
  state: stateProp,
  className,
}: ConversationRunStateLabelProps) {
  const state: ConversationRunState =
    stateProp ??
    deriveConversationRunState({ hasActiveChain, lastChainFailed });

  if (state === 'running') {
    return (
      <ShimmerText className={cx('text-xs font-medium', className)}>
        {RUN_STATE_WORKING_LABEL}
      </ShimmerText>
    );
  }

  if (state === 'failed') {
    return (
      <span
        className={cx(
          'text-xs font-medium text-[var(--color-text-error-primary)]',
          className
        )}
      >
        {RUN_STATE_FAILED_LABEL}
      </span>
    );
  }

  return null;
}

export default ConversationRunStateLabel;
